/**
 * The list query grammar — plan §2.4: "All list endpoints accept the same query
 * grammar (filter, sort, group, fields, aggregate) so the list engine
 * (Section 3.5) is one component."
 *
 * Defined here so the client builds exactly what the server parses. A saved
 * view is one of these objects stored as JSON.
 */
import { z } from 'zod';
import { datePeriod, describePeriod } from './date-periods.js';
export const FILTER_OPERATORS = [
    'eq', 'neq', 'lt', 'lte', 'gt', 'gte',
    'contains', 'starts_with',
    'in', 'not_in',
    'is_null', 'is_not_null',
    'between',
    /*
     * A date window, held as what it means rather than as the days it currently
     * covers — see `date-periods.ts`. It is an operator rather than two
     * conditions because a saved view that froze "last month" into two dates on
     * the day it was saved is a view that goes quietly wrong in September.
     */
    'in_period',
    /*
     * A window measured from today rather than named: "in the last 45 days",
     * "in the next 14", "more than 90 days ago".
     *
     * `in_period`'s presets are the windows a calendar has names for. This is
     * the other half — the ones an operation has names for, where the number is
     * the point. "Invoiced more than 45 days ago" is the collections list, and
     * there is no month it corresponds to.
     */
    'in_relative',
];
/** What an `in_relative` condition holds. */
export const RELATIVE_UNITS = ['days', 'weeks', 'months'];
export const relativeWindow = z.object({
    unit: z.enum(RELATIVE_UNITS),
    amount: z.number().int().min(1).max(3650),
    /**
     * `past` is within the last N (today − N … today), `future` within the next
     * N (today … today + N), and `ago` is everything older than N (< today − N).
     *
     * `ago` is not `past` inverted: "more than 45 days ago" excludes the last
     * 45 days *and* has no lower bound, which is what a collections or a
     * dormancy question actually asks.
     */
    direction: z.enum(['past', 'future', 'ago']),
});
/** How one reads on a chip: "in the last 45 days". */
export function describeRelative(window) {
    const unit = window.amount === 1 ? window.unit.replace(/s$/, '') : window.unit;
    switch (window.direction) {
        case 'past': return `the last ${window.amount} ${unit}`;
        case 'future': return `the next ${window.amount} ${unit}`;
        case 'ago': return `more than ${window.amount} ${unit} ago`;
    }
}
/**
 * A value that stands for whoever is asking, rather than for a row.
 *
 * "Salesperson is me" saved as a shared view has to mean *each viewer*, or
 * it is a view about one person that everybody else has to edit before it is
 * any use. The server resolves these per request from the session and the
 * scope, so the stored filter never contains a user id it should not.
 */
export const FILTER_TOKENS = ['me', 'my_branch', 'today'];
export const tokenValue = z.object({ token: z.enum(FILTER_TOKENS) });
export const isToken = (value) => typeof value === 'object' && value !== null && 'token' in value
    && FILTER_TOKENS.includes(value.token);
export const TOKEN_LABELS = {
    me: 'Me', my_branch: 'My branch', today: 'Today',
};
/** Operators that take no value, so a UI knows not to ask for one. */
export const NULLARY_OPERATORS = ['is_null', 'is_not_null'];
export const filterCondition = z.object({
    field: z.string().min(1),
    op: z.enum(FILTER_OPERATORS),
    value: z.unknown().optional(),
    /**
     * What the ids in `value` were **called** when somebody picked them.
     *
     * Display only. `filterSql` ignores it, `filterKey` ignores it, and the
     * server never validates a value against it — which is the whole reason it
     * is safe to carry: it cannot change what a filter *means*, only what it
     * reads as.
     *
     * It exists because "Company is any of 4f2a…" is a chip nobody can act on.
     * Resolving the names on every page load would be a round trip per filter
     * per surface; storing them beside the ids makes a saved view, a shared
     * link and a card readable the instant they open, and `useRelationLabels`
     * fills in whatever is missing (v3 §4.4).
     *
     * `null` means "looked up, and this person cannot see it" — a different
     * answer from "not looked up yet", and the reason this is a nullable record
     * rather than a plain one. The filter still applies either way.
     */
    labels: z.record(z.string(), z.string().nullable()).optional(),
});
/**
 * The names a filter knows, given what it was told and what has been looked
 * up since.
 *
 * One function for every surface that prints a filter — the toolbar chips,
 * the quick filter's section summaries, the advanced builder's pills and its
 * "Reads as" line, the card readback and the bulk confirm. They each used to
 * reach for `condition.value` and print it, which is how a uuid ends up on
 * screen in five places at once.
 */
export function labelFor(condition, id, cache) {
    const given = condition.labels?.[id];
    if (given !== undefined)
        return given;
    return cache?.get(`${condition.field}\u0000${id}`);
}
/**
 * What a record somebody cannot see is called.
 *
 * Not blank and not the id. The filter is still applying — it is a condition,
 * not a permission — so the sentence has to say *something*, and "a company
 * you can't see" is the true one. It is in shared because the chip, the
 * section summary and the sentence all need the same words, and three copies
 * of a phrase is three phrasings within a release.
 */
export function unseenText(kind) {
    return `A ${kind?.toLowerCase() ?? 'record'} you can\u2019t see`;
}
/** Drops labels for ids the condition no longer names. */
export function pruneLabels(condition) {
    if (!condition.labels)
        return condition;
    const kept = new Set((Array.isArray(condition.value) ? condition.value : [condition.value])
        .filter((one) => typeof one === 'string'));
    const labels = Object.fromEntries(Object.entries(condition.labels).filter(([id]) => kept.has(id)));
    return Object.keys(labels).length > 0
        ? { ...condition, labels }
        : (({ labels: _dropped, ...rest }) => rest)(condition);
}
export const isRelatedCondition = (node) => typeof node.relation === 'string';
export const relatedCondition = z.lazy(() => z.object({
    relation: z.string().min(1),
    match: z.enum(['any', 'none']).default('any'),
    conditions: z.array(z.union([filterCondition, relatedCondition, filterGroup])).default([]),
}));
/**
 * A node is a group when it carries conditions, and a condition otherwise.
 *
 * Worth stating as a function rather than inlining the check: the union below
 * tries `filterCondition` first *because* a bare condition would otherwise
 * parse as an empty group — `conditions` has a default, so zod would happily
 * fill one in and silently turn `status is completed` into "match nothing".
 */
export const isFilterGroup = (node) => Array.isArray(node.conditions)
    // A related condition carries `conditions` too, and is not a group: its
    // conditions belong to another area's catalogue, so anything that walks a
    // group's children would validate them against the wrong one.
    && !isRelatedCondition(node);
/*
 * `unknown` as the input type, not `FilterGroup`. `match` carries a default, so
 * what the schema *accepts* is looser than what it produces, and naming the
 * output type for both halves makes the two disagree.
 */
export const filterGroup = z.lazy(() => z.object({
    match: z.enum(['and', 'or']).default('and'),
    /*
     * `relatedCondition` before `filterCondition`: a related node has no
     * `field`, so it would fail the condition schema, but the union reports the
     * *first* branch's error and "field is required" is a baffling thing to be
     * told about "has a visit where…". Ordered by how specific the shape is.
     */
    conditions: z.array(z.union([relatedCondition, filterCondition, filterGroup])).default([]),
}));
/**
 * A stable identity for a set of conditions.
 *
 * What "is this list filtered exactly like that card?" comes down to. Order is
 * not part of what a filter says, so the parts are sorted; a nested group's
 * identity is its own shape, recursively, so a card whose filter contains a
 * group still matches a view that carries the same group.
 *
 * Comparing the conditions rather than a stored card id is deliberate: a
 * filter somebody built by hand that happens to match a card lights that card
 * up, which is the honest answer to "what am I looking at?".
 */
export function filterKey(nodes) {
    return JSON.stringify([...nodes]
        .map((node) => {
        if (isRelatedCondition(node)) {
            return [node.relation, node.match, filterKey(node.conditions)];
        }
        return isFilterGroup(node)
            ? [node.match, filterKey(node.conditions)]
            : [node.field, node.op, node.value ?? null];
    })
        .sort());
}
/** Every condition in a tree, in reading order — what a "does it mention X" check walks. */
export function flattenConditions(group) {
    if (!group)
        return [];
    return group.conditions.flatMap((node) => {
        /*
         * A related condition's own conditions are *not* returned. They name
         * columns of another area, and every caller of this uses the answer to
         * ask "does this filter mention my field X?" — a visit's `status`
         * answering for a job's would light the wrong chip and hide the wrong
         * column.
         */
        if (isRelatedCondition(node))
            return [];
        return isFilterGroup(node) ? flattenConditions(node) : [node];
    });
}
/**
 * The icon a column wears in its header — list engine v2, "read by shape
 * first: every column has an icon in its header".
 *
 * Named on the field rather than chosen by the screen, because a field has one
 * meaning wherever it appears: Phone is a phone on Contacts and on Employees,
 * and a screen that picked its own would eventually disagree with the other
 * screen showing the same column. It is also what the screen-reader label
 * says the icon means, so there has to be exactly one answer.
 */
export const FIELD_ICONS = [
    'text', 'user', 'company', 'money', 'number', 'percent', 'date', 'status',
    'tag', 'phone', 'email', 'message', 'place', 'job', 'boolean', 'action', 'count',
    /*
     * The second pass. The first seventeen covered the kinds of *data* and left
     * a board where Job number, Job, Customer, Property, Service line and
     * Salesperson all wore the same sheet of paper — which is the opposite of
     * "read by shape first". These name what a column is about.
     */
    'reference', 'hours', 'map', 'route', 'crew', 'equipment', 'invoice', 'quote',
    'note', 'clock', 'calendar_due', 'branch', 'category', 'unit', 'link', 'flag',
];
/** What each icon means, for the label a screen reader reads out. */
export const FIELD_ICON_MEANINGS = {
    text: 'text', user: 'person', company: 'company', money: 'amount', number: 'number',
    percent: 'percentage', date: 'date', status: 'status', tag: 'label', phone: 'phone number',
    email: 'email address', message: 'message', place: 'place', job: 'job', boolean: 'yes or no',
    action: 'action', count: 'count',
    reference: 'reference number', hours: 'hours', map: 'zone', route: 'route', crew: 'crew',
    equipment: 'equipment', invoice: 'invoice', quote: 'quote', note: 'note', clock: 'time',
    calendar_due: 'due date', branch: 'branch', category: 'category', unit: 'unit of measure',
    link: 'link', flag: 'flag',
};
export const sortSpec = z.object({
    field: z.string().min(1),
    direction: z.enum(['asc', 'desc']).default('asc'),
});
/** The Σ functions the per-column menu offers (plan §3.5). */
export const AGGREGATE_FUNCTIONS = ['sum', 'avg', 'min', 'max', 'count'];
export const aggregateSpec = z.object({
    field: z.string().min(1),
    fn: z.enum(AGGREGATE_FUNCTIONS),
});
/** Comfortable, Compact, Spacious — the density control's three settings. */
export const DENSITIES = ['default', 'compact', 'spacious'];
export const listView = z.object({
    filter: filterGroup.optional(),
    sort: z.array(sortSpec).default([]),
    /** The column rows are grouped by — one level, as the mockups draw it. */
    group: z.string().optional(),
    /** Which columns to return; omitted means the area's default set. */
    fields: z.array(z.string()).optional(),
    aggregates: z.array(aggregateSpec).default([]),
    density: z.enum(DENSITIES).default('default'),
    /*
     * Column layout lives in the view rather than in columns on `saved_views`.
     *
     * The view is what goes in the URL, so a layout somebody has tuned travels
     * with the link they send — and there is then one definition behind a saved
     * view, a stat card and an export, which is the thing that stops the three
     * drifting apart.
     */
    columnWidths: z.record(z.string(), z.number().int().min(48).max(1200)).optional(),
    /** Fields the user pinned left, beyond the landing-mark column. */
    pinned: z.array(z.string()).optional(),
});
export function emptyView() {
    return { sort: [], aggregates: [], density: 'default' };
}
/*
 * base64url, without Buffer.
 *
 * This module is imported by the browser as well as the server, and the client
 * is the side that actually puts a view in the URL — `Buffer` would be
 * undefined exactly where it is needed most. `TextEncoder` and `btoa`/`atob`
 * exist in both runtimes, so the encoding is shared rather than polyfilled.
 */
const toBase64Url = (bytes) => {
    let binary = '';
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const fromBase64Url = (encoded) => {
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/')
        .padEnd(Math.ceil(encoded.length / 4) * 4, '=');
    const binary = atob(padded);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};
/**
 * A view survives a round trip through a URL, because the scope selector and
 * the saved-view tabs both put it there.
 */
export function encodeView(view) {
    return toBase64Url(new TextEncoder().encode(JSON.stringify(view)));
}
export function decodeView(encoded) {
    try {
        return listView.parse(JSON.parse(new TextDecoder().decode(fromBase64Url(encoded))));
    }
    catch {
        return emptyView();
    }
}
export const listResult = z.object({
    items: z.array(z.record(z.string(), z.unknown())),
    groups: z.array(z.object({
        key: z.string(),
        label: z.string(),
        count: z.number(),
        aggregates: z.record(z.string(), z.unknown()),
    })).optional(),
    totals: z.record(z.string(), z.unknown()),
    /** Visible count and the total within scope — the page bar's "150 of 1,240". */
    visibleCount: z.number(),
    totalCount: z.number(),
    nextCursor: z.string().nullable(),
});
/**
 * How each operator reads, as a phrase that follows the field name.
 *
 * Comparisons use the symbol rather than words: "Expected > $5,000" is shorter
 * and less ambiguous than "Expected is greater than $5,000", and the builder
 * rows already say the words in their own dropdown.
 */
function phraseFor(condition, write) {
    const list = (values) => Array.isArray(values) ? values.map(write).join(' or ') : write(values);
    switch (condition.op) {
        case 'eq': return `is ${write(condition.value)}`;
        case 'neq': return `is not ${write(condition.value)}`;
        case 'in': return `is ${list(condition.value)}`;
        case 'not_in': return `is not ${list(condition.value)}`;
        case 'contains': return `contains ${write(condition.value)}`;
        case 'starts_with': return `starts with ${write(condition.value)}`;
        case 'is_null': return 'is empty';
        case 'is_not_null': return 'is not empty';
        case 'gt': return `> ${write(condition.value)}`;
        case 'gte': return `≥ ${write(condition.value)}`;
        case 'lt': return `< ${write(condition.value)}`;
        case 'lte': return `≤ ${write(condition.value)}`;
        case 'between': {
            const range = Array.isArray(condition.value) ? condition.value : [];
            return `between ${write(range[0])} and ${write(range[1])}`;
        }
        case 'in_period': {
            const period = datePeriod.safeParse(condition.value);
            return period.success ? `is in ${describePeriod(period.data)}` : 'is in a date window';
        }
        case 'in_relative': {
            const window = relativeWindow.safeParse(condition.value);
            if (!window.success)
                return 'is in a date window';
            // "more than 45 days ago" is already a complete phrase; the other two
            // are nouns that need "is in" in front of them.
            return window.data.direction === 'ago'
                ? `is ${describeRelative(window.data)}`
                : `is in ${describeRelative(window.data)}`;
        }
    }
}
export function describeCondition(condition, labels) {
    const write = (value) => {
        // A token reads as the word somebody picked, not as its wire shape:
        // "salesperson is me", never `salesperson is [object Object]`.
        if (isToken(value))
            return TOKEN_LABELS[value.token].toLowerCase();
        return labels.value ? labels.value(value, condition) : String(value ?? '');
    };
    return `${labels.field(condition.field)} ${phraseFor(condition, write)}`;
}
/** "has a visit where status is open" / "has no invoice where status is unpaid". */
export function describeRelated(node, labels) {
    const name = labels.relation ? labels.relation(node.relation) : node.relation;
    const inner = describeFilter({ match: 'and', conditions: node.conditions }, labels);
    const head = node.match === 'none' ? `has no ${name}` : `has a ${name}`;
    return inner ? `${head} where ${inner}` : head;
}
/**
 * The whole tree as one sentence.
 *
 * Nested groups are parenthesised; the outermost is not, because "(a and b)"
 * as a whole sentence reads like something was left out.
 */
export function describeFilter(group, labels, nested = false) {
    if (!group || group.conditions.length === 0)
        return '';
    const parts = group.conditions
        .map((node) => {
        if (isRelatedCondition(node))
            return describeRelated(node, labels);
        return isFilterGroup(node)
            ? describeFilter(node, labels, true)
            : describeCondition(node, labels);
    })
        .filter((part) => part.length > 0);
    if (parts.length === 0)
        return '';
    const sentence = parts.join(group.match === 'or' ? ' or ' : ' and ');
    // A group of one needs no brackets; bracketing it would suggest a grouping
    // that is not doing anything.
    return nested && parts.length > 1 ? `(${sentence})` : sentence;
}
// ---------------------------------------------------------------------------
// Bulk actions — list engine v3 §3.5
// ---------------------------------------------------------------------------
/**
 * Which records a bulk action is about.
 *
 * Three shapes, and the middle one is the point. `ids` is what somebody
 * ticked; it is capped at a thousand, the same ceiling the client stops
 * ticking at, so the two cannot disagree about what is sendable.
 *
 * `matching` is "everything this question describes". It sends the **question**
 * rather than the answer, because the client does not have the answer — it
 * has fifty rows of four hundred — and because a list of four hundred uuids
 * is both slow and a lie the moment somebody else adds a row. `expect` is the
 * number the person was looking at when they pressed the button: the server
 * refuses if its own count differs, rather than quietly acting on 127 records
 * after the confirm said 126.
 *
 * `batch` is Undo and nothing else: the ids one earlier run actually changed.
 */
export const bulkSelector = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('ids'),
        ids: z.array(z.string().uuid()).min(1).max(1000),
    }),
    z.object({
        mode: z.literal('matching'),
        view: listView,
        search: z.string().optional(),
        asOf: z.string().optional(),
        excluded: z.array(z.string().uuid()).max(5000).default([]),
        /** What the person saw. A mismatch is refused, never guessed at. */
        expect: z.number().int().min(1),
    }),
    z.object({ mode: z.literal('batch'), batchId: z.string().uuid() }),
]);
/**
 * What happened, per record.
 *
 * `skipped` carries a reason each, because "Moved 10 of 12" with no account
 * of the other two is a result nobody can act on — and because the two that
 * did not move are usually the interesting ones.
 */
export const bulkSkip = z.object({
    id: z.string().uuid(),
    label: z.string(),
    reason: z.string(),
});
export const bulkResult = z.object({
    batchId: z.string().uuid(),
    changed: z.number().int(),
    skipped: z.array(bulkSkip),
    /** Whether this action has an exact opposite to offer as Undo. */
    undoable: z.boolean(),
});
export const bulkRequest = z.object({
    selector: bulkSelector,
    input: z.unknown().optional(),
});
//# sourceMappingURL=list-query.js.map